import type { Object3D, SpotLight } from 'three';
import type { HighlightManager } from '../highlight-manager.class';
import type { HighlightConfig } from '../../highlight.class';
import { Highlight } from '../../highlight.class';
import { animateHighlightInCfn } from './animate-highlight-in.cfn';
import { animateTransitionToNewTargetCfn } from './animate-transition-to-new-target.cfn';

/**
 * Parameters for the highlight function
 */
export interface HighlightParams {
	objectId: string;
	object: Object3D;
	addHighlight?: boolean;
	spotlightConfig?: HighlightConfig['spotlight'];
}

/**
 * Highlight an object with spotlight
 *
 * @param self The HighlightManager instance
 * @param params Highlight parameters
 */
export const highlightCfn = (
	self: HighlightManager,
	params: HighlightParams
): void => {
	const { objectId, object, addHighlight = false, spotlightConfig } = params;

	// Access fields via public getters
	const circle = self.circle;
	const highlights = self.highlights;
	const currentHighlightId = self.currentHighlightId;
	const scene = self.scene;
	const debugMode = self.debugMode;
	const maxIntensity = self.maxIntensity;

	if (!circle) {
		console.warn('HighlightManager: Cannot highlight without circle configuration');
		return;
	}

	// Normalize objectId to string for consistent comparison
	const normalizedId = String(objectId);

	// Check if this is the same object - if so, toggle off (only for regular double-click)
	if (currentHighlightId === normalizedId && !addHighlight) {
		self.clearHighlight(normalizedId);
		return;
	}

	// Check if this is a selection change (only when NOT adding)
	const isSelectionChange = !addHighlight &&
		currentHighlightId !== null &&
		currentHighlightId !== normalizedId;

	// Get the previous highlight before any changes
	const previousHighlight = isSelectionChange ? highlights.get(currentHighlightId!) : null;

	// Store previous highlight reference for transitions (only when switching)
	if (isSelectionChange && currentHighlightId) {
		self.previousHighlightId = currentHighlightId;
	}

	// Check if we already have this highlight
	let highlight = highlights.get(normalizedId);

	if (!highlight) {
		if (isSelectionChange && previousHighlight) {
			// SWITCHING: Reuse the previous highlight's spotlight
			// Transfer the spotlight to target the new object
			highlight = new Highlight(object, normalizedId, {
				scene,
				circle,
				debugMode,
				spotlight: spotlightConfig,
				reuseSpotlight: previousHighlight.light // Pass existing spotlight to reuse
			});
			highlights.set(normalizedId, highlight);

			// Remove old highlight (but its spotlight is now owned by the new highlight)
			previousHighlight.disposeWithoutLight(); // Don't dispose the light, just the highlight
			highlights.delete(self.previousHighlightId!);
		} else {
			// ADDING or FIRST: Create new highlight with new spotlight
			const wasEmpty = highlights.size === 0;

			highlight = new Highlight(object, normalizedId, {
				scene,
				circle,
				debugMode,
				spotlight: spotlightConfig
			});
			highlights.set(normalizedId, highlight);

			// Trigger callback when first highlight is added
			if (wasEmpty) {
				self.onFirstHighlightAdded?.();
			}
		}
	}

	// Handle animation
	if (isSelectionChange && previousHighlight) {
		// Switching: animate the (reused) spotlight along arc to new target
		// Pass the previous aim point so the spotlight direction animates smoothly
		animateTransitionToNewTargetCfn(self, {
			highlight,
			previousTarget: previousHighlight.highlightTarget,
			previousSource: previousHighlight.highlightLightSource,
			previousAimPoint: previousHighlight.spotlightAimPoint
		});
	} else {
		// Adding or first highlight: animate in from source
		animateHighlightInCfn(self, { highlight });
	}

	// Ensure spotlight is visible and has minimum intensity for immediate visibility
	if (highlight.light) {
		highlight.light.visible = true;
		// If intensity is 0, set a minimum so light is visible immediately
		if (highlight.light.intensity === 0) {
			highlight.setIntensity(maxIntensity * 0.1); // Start at 10% intensity
		}
	}

	self.currentHighlightId = normalizedId;

	// Log state for debugging (commented out)
	// const allSpotlights = scene.children.filter((child) => child instanceof SpotLight && child.name.startsWith('highlight-spotlight-'));
};
